// app/(dashboard)/page.tsx
"use client";

import { useState, useMemo } from "react";
import {
  MOCK_STATS,
  MOCK_RECENT_ORDERS,
  MOCK_SALES_CHART,
  MOCK_ACTIVITIES,
  MOCK_TRAFFIC_SOURCES,
} from "@/lib/mock";
import { StatCard } from "@/components/dashboard/StatCard";
import { SalesChart } from "@/components/dashboard/SalesChart";
import { OrdersChart } from "@/components/dashboard/OrdersChart";
import { ActivityTimeline } from "@/components/dashboard/ActivityTimeline";
import { TrafficSources } from "@/components/dashboard/TrafficSources";
import { DataTable } from "@/components/shared/DataTable"; // ⬅️ اضافه شد
import { ColumnDef } from "@tanstack/react-table"; // ⬅️ اضافه شد
import { Trophy, ShoppingCart, Users, Package, DollarSign, Hand, Rocket, TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";

// ۱. تعریف وضعیت‌ها برای ستون جدول
const STATUS_MAP: Record<string, { label: string; className: string }> = {
  pending: { label: "در انتظار", className: "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400" },
  confirmed: { label: "تأیید شده", className: "bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400" },
  shipping: { label: "در حال ارسال", className: "bg-cyan-50 text-cyan-700 dark:bg-cyan-500/10 dark:text-cyan-400" },
  delivered: { label: "تحویل شده", className: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400" },
  canceled: { label: "لغو شده", className: "bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400" },
};

export default function DashboardPage() {
  const [stats] = useState(MOCK_STATS);
  const [recentOrders] = useState(MOCK_RECENT_ORDERS);
  const [salesChart] = useState(MOCK_SALES_CHART);
  const [activities] = useState(MOCK_ACTIVITIES);
  const [trafficSources] = useState(MOCK_TRAFFIC_SOURCES);

  // ۲. تعریف ستون‌های جدول داشبورد
  const dashboardOrderColumns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "customerName",
        header: "مشتری",
        cell: ({ row }) => (
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">
              {row.original.customerAvatar}
            </div>
            <span className="font-medium text-sm text-foreground">
              {row.original.customerName}
            </span>
          </div>
        ),
      },
      {
        accessorKey: "id",
        header: "شماره سفارش",
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.id}
          </span>
        ),
      },
      {
        accessorKey: "amount",
        header: "مبلغ",
        cell: ({ row }) => (
          <span className="font-bold text-sm text-foreground">
            {row.original.amount.toLocaleString("fa-IR")}
            <span className="text-xs text-muted-foreground font-normal mr-1">ت</span>
          </span>
        ),
      },
      {
        accessorKey: "status",
        header: "وضعیت",
        cell: ({ row }) => {
          const status = STATUS_MAP[row.original.status];
          return (
            <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold ${status.className}`}>
              {status.label}
            </span>
          );
        },
      },
    ],
    []
  );

  return (
    <div className="space-y-6 animate-fade-in max-w-[1600px] mx-auto">
      {/* ─── ردیف اول: بنر + خلاصه تراکنش‌ها ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* ... (کدهای بنر بدون تغییر ماند) ... */}
        <div className="lg:col-span-4 materio-card p-6 flex flex-col justify-between relative overflow-hidden">
          <div>
            <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
              سلام، مدیر عزیز <Hand className="w-5 h-5 text-amber-500" strokeWidth={2} />
            </h2>
            <p className="text-sm text-muted-foreground mt-1">بهترین فروشنده این ماه</p>
            <h3 className="text-3xl font-bold text-primary mt-3">
              ۱۲,۵۰۰,۰۰۰ <span className="text-base font-normal text-muted-foreground">تومان</span>
            </h3>
            <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1.5">
              ۷۸٪ از هدف <Rocket className="w-4 h-4 text-primary" strokeWidth={2.2} />
            </p>
          </div>
          <Trophy className="absolute left-4 bottom-4 w-20 h-20 text-amber-400 opacity-20" />
        </div>

        <div className="lg:col-span-8 materio-card p-6 flex flex-col justify-between">
          {/* ... (کدهای خلاصه تراکنش‌ها بدون تغییر ماند) ... */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-base font-bold text-foreground">خلاصه تراکنش‌ها</h3>
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                مجموع ۴۸.۵٪ رشد <TrendingUp className="w-4 h-4 text-brand-500" strokeWidth={2.2} /> در این ماه
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {stats.map((stat) => {
              const Icon = stat.id === "revenue" ? DollarSign : stat.id === "orders" ? ShoppingCart : stat.id === "products" ? Package : Users;
              return (
                <div key={stat.id} className="flex items-center gap-3">
                  <div className={cn("w-12 h-12 rounded-lg flex items-center justify-center shrink-0 text-white", stat.solidColor)}>
                    <Icon className="h-6 w-6" strokeWidth={2} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground truncate mb-0.5">{stat.title}</p>
                    <p className="text-base font-bold truncate text-foreground">{stat.value}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ─── ردیف دوم: ۴ کارت آمار ─── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <StatCard key={stat.id} stat={stat as any} />
        ))}
      </div>

      {/* ─── ردیف سوم: نمودارها ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SalesChart data={salesChart} />
        <OrdersChart data={salesChart} />
      </div>

      {/* ─── ردیف چهارم: جدول (تغییر یافته) + ترافیک + فعالیت ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          
          {/* ⬇️ جایگزینی RecentOrdersTable با DataTable ⬇️ */}
          <div className="h-full">
            <DataTable
              columns={dashboardOrderColumns}
              data={recentOrders}
              title="سفارشات اخیر"
              showPagination={false} // در داشبورد معمولاً پاجینیشن لازم نیست
              enableColumnFilters={false} // فیلتر زیر ستون‌ها نیاز نیست
              className="h-full border-0 shadow-none" // حذف دوبل شدن حاشیه کارت
            />
          </div>
          
        </div>
        <div className="space-y-6 flex flex-col">
          <TrafficSources sources={trafficSources} />
          <ActivityTimeline activities={activities} />
        </div>
      </div>
    </div>
  );
}